import PageTitle from "components/pageTitle";
import DataTable, { Column } from "components/DataTable";
import { StatusChip } from "components/StatusChip";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { conductLegislativeDrafting } from "types";
import TabNav from "../../components/TabNav";
import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";
import Button from "components/buttons";
import { getLegislativeDraftings } from "services/legislativeService";
import {
  LAW_IMPLEMENTATION_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
} from "utils/constantValues";
import { useToast } from "contexts/ToastContext";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { getLegalAdvisories } from "services/legalAdvisoryService";
import {
  getAuditLawImplementation,
  getAuditLawImplementations,
  getMyTeamAudits,
  getLawImplementationsByRegistryAndUser,
  getLawImplementationsByRegistryNumber,
} from "services/lawImplementationService";
import { useTranslation } from "react-i18next";
import { expandRoles } from "utils/authRules";

// Roles that see the whole admin unit's follow-ups rather than only their own assignments.
const UNIT_WIDE_ROLES = [
  "LegalResearchCoordinator",
  "LRDIFUDDirector",
  "GeneralAttorney",
  "ZonalLIF",
  "Registrar",
];

const LawImplementation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contextData = useAuth();
  const isRegistrar = contextData?.user?.roles?.some((r: string) => r === "Registrar");
  // Any prosecutor flavour (LegalResearch / ImpAwareness / LegalTranslation) only sees the
  // follow-ups whose team they are on — unless they also hold a unit-wide role.
  const userRoles: string[] = contextData?.user?.roles || [];
  const isProsecutor =
    expandRoles(userRoles).includes("LegalResearchProsecutor") &&
    !userRoles.some((r: string) => UNIT_WIDE_ROLES.includes(r));
  const [activeTab, setActiveTab] = useState<string>(isRegistrar ? (t("NewRequest") || "New Request") : "All");
  const tabs = isRegistrar
    ? [t("NewRequest") || "New Request", t("Registered") || "Registered"]
    : [t("All") || "All", t("NewRequest") || "New Request", t("SectorRequest") || "Sector Request", t("ZonalRequest") || "Zonal Request"];
  const [tabFilteredData, setTabFilteredData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });
  const [data, setData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });
  const [targetData, setTargetData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });
  const [filteredTargetData, setFilteredTargetData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });
  const [filteredData, setFilteredData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });
  const { showToast } = useToast();

  const columns: Column<conductLegislativeDrafting>[] = useMemo(() => [
    {
      header: t("ApplicationNumber") || "Application Number",
      accessor: "application_number",
      // For the Sector Request (audit) rows the parent's application number is stored in registry_number.
      render: (row: any) => <p>{row?.application_number || row?.registry_number || "-"}</p>,
      widthClass: "w-[15%]",
    },
    {
      header: t("FollowupDate") || "Follow-up Date",
      accessor: "created_at",
      render: (row) => (
        <p>
          {row?.created_at
            ? new Date(row?.created_at).toLocaleDateString()
            : "-"}
        </p>
      ),
      widthClass: "w-[15%]",
    },
    {
      header: t("Status") || "Status",
      accessor: "current_approval_status",
      render: (row) => {
        const statusObj = Object.values(LAW_IMPLEMENTATION_REQUEST_STATUS).find(
          (s) => s.value === (row?.current_approval_status ?? row?.current_status),
        );
        return (
          <StatusChip
            status={statusObj?.label || row?.current_approval_status || ""}
          />
        );
      },
    },
    {
      header: t("ForwardStatus") || "Forward Status",
      accessor: "forward_status",
      render: (row) => {
        const statusObj = Object.values(
          LEGISLATIVE_DRAFTING_REQUEST_STATUS,
        ).find((s) => s.value === row?.forward_status);
        return (
          <StatusChip status={statusObj?.label || row?.current_status || ""} />
        );
      },
    },
    {
      header: t("ViewAction") || "View Action",
      accessor: "action",
      render: (row: any) => (
        <div
          className="flex gap-2"
          onClick={() => {
            isRegistrar ||
            activeTab === "Sector Request" ||
            activeTab === "Zonal Request"
              ? navigate(`audit/${row?.id}`)
              : navigate(`${row?.id}`);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="22"
            viewBox="0 0 17 22"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 0V5.5C10 5.89782 10.158 6.27936 10.4393 6.56066C10.7206 6.84196 11.1022 7 11.5 7H17V14C17 14.5304 16.7893 15.0391 16.4142 15.4142C16.0391 15.7893 15.5304 16 15 16H13V18C13 18.5304 12.7893 19.0391 12.4142 19.4142C12.0391 19.7893 11.5304 20 11 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V6C0 5.46957 0.210714 4.96086 0.585786 4.58579C0.960859 4.21071 1.46957 4 2 4H4V2C4 1.46957 4.21071 0.960859 4.58579 0.585786C4.96086 0.210714 5.46957 0 6 0H10ZM4 6H2V18H11V16H6C5.46957 16 4.96086 15.7893 4.58579 15.4142C4.21071 15.0391 4 14.5304 4 14V6ZM12 0.043C12.3789 0.123355 12.7263 0.311992 13 0.586L16.414 4C16.688 4.27373 16.8766 4.62112 16.957 5H12V0.043Z"
              fill="#212121"
            />
          </svg>
          <svg
            className="my-auto"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <mask
              id="mask0_9_31087"
              maskUnits="userSpaceOnUse"
              x="1"
              y="5"
              width="22"
              height="14"
            >
              <path
                d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                fill="white"
                stroke="white"
                strokeWidth="1.33333"
                strokeLinejoin="round"
              />
              <path
                d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                fill="black"
                stroke="black"
                strokeWidth="1.33333"
                strokeLinejoin="round"
              />
            </mask>
            <g mask="url(#mask0_9_31087)">
              <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
            </g>
          </svg>
        </div>
      ),
    },
  ], [activeTab, isRegistrar, navigate, t]);

  useEffect(() => {
    // Prosecutors only get the sector/zonal audits their team is assigned to.
    (isProsecutor ? getMyTeamAudits() : getAuditLawImplementations())
      .then((data) => {
        setTargetData((prev) => {
          return { ...prev, data: Array.isArray(data) ? data : [] };
        });
      })
      .catch((error) => {
        console.error("Error fetching legal research requests:", error);
        showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
      })
      .finally(() => {
        setTargetData((prev) => ({
          ...prev,
          isLoading: false,
        }));
      });
  }, [isProsecutor, showToast, t]);

  useEffect(() => {
    // Prosecutors (any prosecutor role) - use the membership-based user filter
    const registryNumber = contextData?.user?.admin_unit || "";
    const userId = contextData?.user?.user_id || "";
    
    if (isProsecutor) {
      // Use registry and user filter for prosecutors
      getLawImplementationsByRegistryAndUser(registryNumber, userId)
        .then((data: any) => {
          console.log("API Response data:", data);
          setData((prev) => {
            return { ...prev, data: data };
          });
          console.log(data);
        })
        .catch((error: any) => {
          console.error("Error fetching legal research requests:", error);
          showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
        })
        .finally(() => {
          setData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        });
    } else {
      // Use registry number filter for other users (including ZonalLIF)
      getLawImplementationsByRegistryNumber(registryNumber)
        .then((data: any) => {
          console.log("API Response data:", data);
          setData((prev) => {
            return { ...prev, data: data };
          });
          console.log(data);
        })
        .catch((error: any) => {
          console.error("Error fetching legal research requests:", error);
          showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
        })
        .finally(() => {
          setData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        });
    }
  }, [contextData?.user, isProsecutor, showToast, t]);

  useEffect(() => {
    if (!data?.data) return;

    Promise.all(
      data.data.map(async (research) => {
        if (!research.requested_by_id) return research;

        let user;
        try {
          const users = await getEmployeesByAdminAndOrg({
            bearer:
              contextData.token ??
              contextData?.user?.access_token ??
              contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
          });
          const match = users.find(
            (u: { roles: string[]; user: any }) =>
              u.user.id === research.requested_by_id,
          );
          user = match?.user;
        } catch (error) {
          console.error(error);
        }
        return {
          ...research,
          requested_by_user: user,
        };
      }),
    ).then((enriched) => {
      console.log("Filtered data:", enriched);
      setFilteredData({
        isLoading: false,
        data: enriched.filter(Boolean) as conductLegislativeDrafting[],
      });
    });
  }, [data.data, contextData.token, contextData.user]);

  useEffect(() => {
    if (!targetData?.data) return;

    Promise.all(
      targetData.data.map(async (research: any) => {
        let user;
        if (research.requested_by_id) {
        try {
          const users = await getEmployeesByAdminAndOrg({
            bearer:
              contextData.token ??
              contextData?.user?.access_token ??
              contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
          });
          const match = users.find(
            (u: { roles: string[]; user: any }) =>
              u.user.id === research.requested_by_id,
          );
          user = match?.user;
        } catch (error) {
          console.error(error);
        }
        }

        // Find match in main implementation data to get application number
        // Using String() comparison to avoid type mismatches (e.g. "1" vs 1)
        const match = filteredData.data.find(
          (d: any) => 
            String(d.id) === String(research.followup?.id || research.followup || research.followup_id)
        );

        return {
          ...research,
          requested_by_user: user,
          application_number: research.application_number || match?.application_number,
        };
      }),
    ).then((enriched) => {
      setFilteredTargetData({
        isLoading: false,
        data: enriched.filter(Boolean) as conductLegislativeDrafting[],
      });
    });
  }, [targetData.data, filteredData.data, contextData.token, contextData.user]);

  const currentTabData = useMemo(() => {
    let tabData;
    // Registrar: view all the audit (sector) requests, split into
    // New Request (no reference yet) and Registered (reference assigned).
    if (isRegistrar) {
      return filteredTargetData.data.filter((req: any) => {
        const hasReference = !!(
          req?.adminn_unit_id ||
          req?.letter_reference_number ||
          req?.current_status === LAW_IMPLEMENTATION_REQUEST_STATUS.letter_refnum_submitted.value
        );
        if (activeTab === (t("Registered") || "Registered")) return hasReference;
        return !hasReference; // New Request
      });
    }
    if (activeTab === t("NewRequest") || activeTab === "New Request") {
      tabData = filteredData.data.filter(
        (req: any) =>
          // The follow-up model has no `status` field; its workflow state is current_approval_status.
          req.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.pending.value,
      );
    } else if (activeTab === t("SectorRequest") || activeTab === "Sector Request") tabData = filteredTargetData.data;
    else if (activeTab === t("ZonalRequest") || activeTab === "Zonal Request") {
      // Zonal audits live on the sector (audit) records — filter those by is_zonal_audit.
      tabData = filteredTargetData.data.filter(
        (req: any) => req.is_zonal_audit === true,
      );
    }
    else tabData = filteredData.data;
    return tabData || [];
  }, [activeTab, filteredData.data, filteredTargetData.data, isRegistrar, t]);

  return (
    <div className="mx-auto flex flex-col space-y-4">
      <PageHeader title={t("LawImplementationFollowUp") || "Law Implementation Follow-Up"} />
      <div className="flex">
        <div className="mt-auto">
          <TabNav active={activeTab} tabs={tabs} onSelect={setActiveTab} />
        </div>
        {(contextData?.user?.roles?.some(
          (p) => p === "LegalResearchCoordinator",
        ) ||
          contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ||
          contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
          contextData?.user?.roles?.some((p) => p === "ZonalLIF")) && (
          <div className="ml-auto">
            <Button
              onClick={() => navigate("request")}
              variant="primary"
              type="button"
            >
              {t("CreateNewRequest") || "Create New Request"}
            </Button>
          </div>
        )}
      </div>

      <DataTable
        title={`< ${t("RequestTable") || "Request Table"}`}
        columns={columns}
        data={currentTabData}
        rowKey={(row) => row.id}
        onSearch={(q) => {}}
        pagination={true}
      />
    </div>
  );
};

export default LawImplementation;